import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ArrowRight, PlayCircle } from 'lucide-react';
import {
    ACTIVE_FLOWS_EVENT,
    activeFlowExpiresAt,
    activeFlowLabel,
    activeFlowPath,
    clearPauseNotice,
    hasPauseNotice,
    readActiveFlows,
} from './activeFlows';
import { formatExpiry } from '../Tasks/progressExpiry';
import './ResumeFlowBar.css';

/**
 * Slim banner on every authenticated page while a guided flow is in progress,
 * so a trainer who clicked off mid-walk can get back into it. It reads only the
 * browser-local markers the flow pages write (no network call per page); the
 * flow page itself reconciles them with the server run when it opens. Expired
 * markers never reach the bar (`readActiveFlows` drops them), and a live one
 * says when its progress dies. The bar hides on the flow's own route, where
 * Resume would be a no-op.
 */
const ResumeFlowBar = () => {
    const location = useLocation();
    const [flows, setFlows] = useState([]);
    // The pathname the notice was consumed on, so it stays a one-shot: the bar
    // is mounted once for the session, and remembering only "paused" would
    // print it on every page the trainer opens afterwards. Read in the
    // initializer so StrictMode's throwaway first pass in dev cannot swallow
    // the notice before the surviving one renders.
    const [pausedPath, setPausedPath] = useState(() => (hasPauseNotice() ? location.pathname : ''));

    // Pause navigates rather than remounting the bar, so a notice set after the
    // first render arrives as a pathname change. Consumed here, once shown.
    useEffect(() => {
        setFlows(readActiveFlows());
        if (hasPauseNotice()) {
            setPausedPath(location.pathname);
            clearPauseNotice();
            return;
        }
        // Leaving the page it was shown on retires it. Kept as a pathname it would come back
        // every time the trainer returned to that page for the rest of the session.
        setPausedPath((prev) => (prev && prev !== location.pathname ? '' : prev));
    }, [location.pathname]);

    useEffect(() => {
        const sync = () => setFlows(readActiveFlows());
        window.addEventListener(ACTIVE_FLOWS_EVENT, sync);
        window.addEventListener('storage', sync);
        return () => {
            window.removeEventListener(ACTIVE_FLOWS_EVENT, sync);
            window.removeEventListener('storage', sync);
        };
    }, []);

    const rows = useMemo(
        () => flows
            .map((entry) => ({
                kind: entry.kind,
                label: activeFlowLabel(entry),
                expiry: formatExpiry(activeFlowExpiresAt(entry)),
                path: activeFlowPath(entry),
            }))
            .filter((row) => row.label && row.path && row.path !== location.pathname),
        [flows, location.pathname]
    );

    if (!rows.length) return null;

    const paused = Boolean(pausedPath) && pausedPath === location.pathname;

    return (
        <div className="rfb" role="status">
            {paused && <span className="rfb-paused">Paused. Resume any time from the Hero Trainer Center.</span>}
            {rows.map((row) => (
                <div key={row.kind} className="rfb-row">
                    <span className="rfb-label"><PlayCircle size={14} /> {row.label}</span>
                    {row.expiry && (
                        <>
                            <span className="rfb-dot" aria-hidden>·</span>
                            <span className="rfb-expiry">{row.expiry}</span>
                        </>
                    )}
                    <span className="rfb-dot" aria-hidden>·</span>
                    <Link to={row.path} className="rfb-resume">Resume <ArrowRight size={13} /></Link>
                </div>
            ))}
        </div>
    );
};

export default ResumeFlowBar;
